import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteCookie, getCookie, setCookie } from "../utils/cookies";
import { getUserDetails, hitRefreshToken, userLogin } from "../apis/user";
import { LoginData, User } from "../utils/types";
import { setAccessTokenForInterceptor } from "../utils/axiosUtil";

interface UserDataContextInterface {
  handleLogin: ({ email, password }: LoginData, event: any) => Promise<void>;
  userID: string | undefined;
  handleLogout: () => Promise<any>;
  userData: User | undefined;
  setUserData: React.Dispatch<React.SetStateAction<User | undefined>>;
  accessToken: string | undefined;
  setAccessToken: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const UserDataContext = createContext({} as UserDataContextInterface);

type UserDataProviderProps = {
  children?: React.ReactNode;
};

const UserContext = ({ children }: UserDataProviderProps) => {
  const navigate = useNavigate();
  const userID = getCookie("userID");

  const [userData, setUserData] = useState<User>();
  const [accessToken, setAccessToken] = useState<string | undefined>();

  useEffect(() => {
    const updateInterceptor = async () => {
      await setAccessTokenForInterceptor(accessToken);
    };
  
    updateInterceptor();
  }, [accessToken]);

  useEffect(() => {
    const onAuthStateChanged = async () => {
    const currentPath = window.location.pathname;
    const isQrSigninRoute = /^\/qrsignin\/[^/]+$/.test(currentPath);

    console.log("Current Path:", currentPath);
    console.log("Is QR Signin Route:", isQrSigninRoute);
      if (isQrSigninRoute) {
        console.log("QR Signin route detected, skipping user data fetch.");
        return;
      }

      if (!userID) {
        console.log("user not present, logged out.");
        deleteCookie("userID");
        navigate("/");
      }else if(!accessToken && userID){
        const refresh_data = await hitRefreshToken();
        if (refresh_data?.status === 200) {
          console.log("Refresh");
          // The interceptor reads a module variable, not React state, so it has
          // to be primed here: setAccessToken only reaches it on the next
          // render, which is too late for the call on the very next line.
          setAccessTokenForInterceptor(refresh_data.data.result.access_token);
          setAccessToken(refresh_data.data.result.access_token);
          const user_data = await getUserDetails(userID);
          if (user_data?.status === 200) setUserData(user_data.data.result);
        }else{
          console.log("Refresh Token Error");
          deleteCookie("userID");
          navigate("/");
        }

      }
      
      else if (accessToken && userID) {
        const user_data = await getUserDetails(userID );

        if (user_data?.status === 200) {
          setUserData(user_data.data.result);
        } else if (user_data?.status === 403) {
          const refresh_data = await hitRefreshToken();
          if (refresh_data?.status === 200) {
            console.log("Refresh");
            // Prime the interceptor before the retry — see note above.
            setAccessTokenForInterceptor(refresh_data.data.result.access_token);
            setAccessToken(refresh_data.data.result.access_token);
            const user_data = await getUserDetails(userID);
            if (user_data?.status === 200) setUserData(user_data.data.result);
          }else{
            console.log("Refresh Token Error");
            deleteCookie("userID");
            navigate("/");
          }
        } else {
          deleteCookie("userID");
          navigate("/");
        }
      } else console.error("User ID invalid or not present.");
    };

    onAuthStateChanged();
  }, [accessToken, userID, navigate]);

  const handleLogin = async ({ email, password }: LoginData, event: any) => {
    event.preventDefault();
    if (!email) {
      toast.error("Invalid email.");
    } else if (password.length <= 7) {
      toast.error("Password needs to have more than 8 characters.");
    } else {
      const res = await userLogin({ email, password });
      if (res?.status === 200) {
        const loginData = res.data.result;
        toast.success("Sign in successful!");
        // Prime the interceptor before getUserDetails below — see note above.
        setAccessTokenForInterceptor(loginData.access_token);
        setAccessToken(loginData.access_token);
        setCookie("userID", loginData.user_id, 7);

        const user_data = await getUserDetails(loginData.user_id);
        if (user_data?.status === 200) setUserData(user_data.data.result);
      } else toast.error("Wrong email/password entered.");
    }
  };

  const handleLogout = async () => {
    deleteCookie("userID");
    setUserData(undefined);

    navigate("/");
  };

  return (
    <UserDataContext.Provider
      value={{
        handleLogin,
        userID,
        userData,
        setUserData,
        handleLogout,
        accessToken,
        setAccessToken
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export function useUserData() {
  return useContext(UserDataContext);
}

export default UserContext;